package com.example.monetix.ui.screens

import androidx.compose.runtime.*
import com.example.monetix.db.*
import com.example.monetix.utils.MonthlyStats
import kotlinx.datetime.LocalDate
import kotlin.math.abs

@Composable
fun rememberRealAvailableBalance(
    latest: List<TransactionEntity>,
    todayEpoch: Long,
    activeAccountId: String?,
    payPeriodStart: LocalDate,
    payPeriodEnd: LocalDate,
    accounts: List<AccountEntity>,
    recurringItems: List<RecurringExpenseEntity>,
    stats: MonthlyStats
): Long {
    val periodEndEpoch = payPeriodEnd.toEpochDays().toLong()

    val emergencyTxs = remember(latest) {
        latest.filter { !it.isDeleted && (it.accountId == "sys_emergency_fund" || it.toAccountId == "sys_emergency_fund" || it.fromAccountId == "sys_emergency_fund") }
    }
    
    val emergencyBalanceCents = remember(emergencyTxs, todayEpoch, activeAccountId) {
        var sum = 0L
        for (tx in emergencyTxs) {
            if (tx.dateEpochDay > todayEpoch) continue

            if (activeAccountId == null) {
                // Vista Globale
                when (tx.type) {
                    "INCOME" -> if (tx.accountId == "sys_emergency_fund") sum += tx.amountCents
                    "EXPENSE" -> if (tx.accountId == "sys_emergency_fund") sum -= tx.amountCents
                    "TRANSFER" -> {
                        if (tx.fromAccountId == "sys_emergency_fund") sum -= tx.amountCents
                        if (tx.toAccountId == "sys_emergency_fund") sum += tx.amountCents
                    }
                }
            } else {
                // Vista per Conto: Quota dello scudo originata da questo conto
                if (tx.type == "TRANSFER") {
                    if (tx.fromAccountId == activeAccountId && tx.toAccountId == "sys_emergency_fund") {
                        sum += tx.amountCents
                    } else if (tx.fromAccountId == "sys_emergency_fund" && tx.toAccountId == activeAccountId) {
                        sum -= tx.amountCents
                    }
                }
            }
        }
        sum
    }

    val baseAvailable = remember(latest, accounts, activeAccountId, periodEndEpoch, todayEpoch) {
        var sum = 0L
        val activeAccountIds = accounts.filter { !it.isDeleted }.map { it.id }.toSet()

        for (tx in latest) {
            if (tx.isDeleted || tx.dateEpochDay > periodEndEpoch) continue

            val isToSavingsAcc = tx.type == "TRANSFER" && tx.toAccountId != null && accounts.any { it.id == tx.toAccountId && it.isSavings }
            val isFutureSavings = tx.dateEpochDay > todayEpoch && (
                    tx.merchantKey?.contains("[pb:", ignoreCase = true) == true ||
                            tx.merchantKey?.contains("[sav:", ignoreCase = true) == true ||
                            tx.merchantKey?.contains("[goal:", ignoreCase = true) == true ||
                            tx.merchantKey?.contains("[pac_sav:", ignoreCase = true) == true ||
                            tx.merchantKey?.contains("salvadanaio", ignoreCase = true) == true ||
                            tx.description?.contains("[pb:", ignoreCase = true) == true ||
                            tx.description?.contains("[sav:", ignoreCase = true) == true ||
                            tx.description?.contains("[goal:", ignoreCase = true) == true ||
                            tx.description?.contains("[pac_sav:", ignoreCase = true) == true ||
                            tx.description?.contains("salvadanaio", ignoreCase = true) == true ||
                            isToSavingsAcc
                    )
            if (isFutureSavings) continue

            if (activeAccountId == null) {
                val isMainActive = tx.accountId in activeAccountIds
                val isFromActive = tx.fromAccountId in activeAccountIds
                val isToActive = tx.toAccountId in activeAccountIds

                when (tx.type) {
                    "INCOME" -> if (isMainActive && tx.dateEpochDay <= todayEpoch) sum += tx.amountCents
                    "EXPENSE" -> if (isMainActive) sum -= tx.amountCents
                    "TRANSFER" -> {
                        if (isFromActive && !isToActive) sum -= tx.amountCents
                        else if (!isFromActive && isToActive && tx.dateEpochDay <= todayEpoch) sum += tx.amountCents
                    }
                }
            } else {
                when (tx.type) {
                    "INCOME" -> if (tx.accountId == activeAccountId && tx.dateEpochDay <= todayEpoch) sum += tx.amountCents
                    "EXPENSE" -> if (tx.accountId == activeAccountId) sum -= tx.amountCents
                    "TRANSFER" -> {
                        if (tx.fromAccountId == activeAccountId) sum -= tx.amountCents
                        if (tx.toAccountId == activeAccountId) sum += tx.amountCents
                    }
                }
            }
        }
        sum
    }

    val totalCartelleMese = remember(recurringItems, activeAccountId, periodEndEpoch) {
        val activeCartelleList = recurringItems.filter { rec ->
            val isCartella = rec.notes?.contains("[CART:", ignoreCase = true) == true || rec.notes?.contains("[TAX:", ignoreCase = true) == true || rec.id.startsWith("cart_") || rec.id.startsWith("tax_")
            val matchesAccount = activeAccountId == null || rec.accountId == activeAccountId || rec.accountId == "NEW_TAX_MODE" || rec.accountId.isNullOrBlank()
            !rec.isDeleted && isCartella && matchesAccount
        }.mapNotNull { rec ->
            val match = Regex("\\[(?:CART|TAX):([^\\]]*)\\]", RegexOption.IGNORE_CASE).find(rec.notes ?: "")
            val rates = match?.groupValues?.get(1)?.split(",")?.map { it.trim() }?.filter { it.isNotBlank() } ?: emptyList()
            val firstUnpaid = rates.firstOrNull { !it.endsWith(":P", ignoreCase = true) && !it.endsWith(":X", ignoreCase = true) }
            val baseAmtStr = firstUnpaid?.split("@")?.firstOrNull() ?: firstUnpaid
            val amt = baseAmtStr?.replace(Regex(":[PX]", RegexOption.IGNORE_CASE), "")?.toLongOrNull() ?: 0L
            val isPaidThisMonth = rec.nextEpochDay > periodEndEpoch
            if (amt > 0L && !isPaidThisMonth) (rec.name) to amt else null
        }
        activeCartelleList.fold(0L) { acc, it -> acc + it.second }
    }

    val unpaidFixed = remember(recurringItems, latest, activeAccountId, periodEndEpoch, payPeriodStart, accounts, stats.unpaidFinancingList) {
        val pStartEpoch = payPeriodStart.toEpochDays().toLong()
        val recScadenze = recurringItems.filter { rec ->
            val isCartella = rec.notes?.contains("[CART:", ignoreCase = true) == true || rec.notes?.contains("[TAX:", ignoreCase = true) == true || rec.id.startsWith("cart_") || rec.id.startsWith("tax_")

            !rec.isDeleted && rec.isEnabled && rec.type != "INCOME" && rec.nextEpochDay <= periodEndEpoch &&
                    (activeAccountId == null || rec.accountId == activeAccountId) &&
                    !isCartella
        }.mapNotNull { rec ->
            val hasPaidThisMonth = latest.any { tx ->
                !tx.isDeleted && tx.dateEpochDay >= pStartEpoch &&
                        (tx.description?.contains("[rec:${rec.id}]", ignoreCase = true) == true || tx.merchantKey?.contains("[rec:${rec.id}]", ignoreCase = true) == true)
            }
            if (hasPaidThisMonth) return@mapNotNull null
            (rec.name) to rec.amountCents
        }

        val finScadenze = stats.unpaidFinancingList.filter { activeAccountId == null || it.accountId == activeAccountId }.map {
            (it.name) to (it.installmentAmountCents + it.commissionCents)
        }
        val activeScadenzeMeseList = recScadenze + finScadenze
        activeScadenzeMeseList.fold(0L) { acc, it -> acc + it.second }
    }

    val totalScadenzeMese = unpaidFixed + totalCartelleMese
    val rawAvailable = baseAvailable - totalScadenzeMese
    val deficit = if (rawAvailable < 0L) abs(rawAvailable) else 0L
    val scudoIntervento = minOf(deficit, emergencyBalanceCents)

    return rawAvailable + scudoIntervento
}
